import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import { Product, Category } from '../../types';
import ProductFormModal from '../../components/admin/ProductFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import './Admin.css';

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  const columnDefs: ColDef[] = [
    // ID
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70, 
      sortable: true,
      filter: 'agNumberColumnFilter'
    },
    
    // Image Preview
    {
      headerName: 'Image',
      field: 'image',
      width: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (params.value) {
          return (
            <img 
              src={params.value} 
              alt="Product" 
              style={{
                width: '50px',
                height: '50px',
                objectFit: 'cover',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }} 
            />
          );
        }
        return (
          <div style={{
            width: '50px',
            height: '50px',
            background: '#f3f4f6',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            border: '1px solid #e5e7eb'
          }}>
            📦
          </div>
        );
      }
    },
    
    // Product Name
    { 
      field: 'name_bn', 
      headerName: 'Product Name', 
      sortable: true, 
      filter: 'agTextColumnFilter', 
      flex: 4,
      minWidth: 350,
      cellRenderer: (params: any) => {
        const productSlug = params.data?.slug;
        const productName = params.value || params.data?.name_en;
        
        if (productSlug) {
          return (
            <a 
              href={`/products/${productSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#1f2937',
                fontWeight: '500',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#3b82f6';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1f2937';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              {productName}
            </a>
          );
        }
        return <span style={{ fontWeight: '500', color: '#1f2937' }}>{productName}</span>;
      }
    },
    
    // Category
    {
      field: 'category_name',
      headerName: 'Category',
      width: 150,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: { color: '#6b7280' }
    },
    
    // Price
    { 
      field: 'price', 
      headerName: 'Price', 
      sortable: true, 
      width: 120,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => `৳${Number(params.value).toFixed(2)}`,
      cellStyle: { fontWeight: '800', color: '#10b981' }
    },
    
    // Discount Price
    {
      field: 'discount_price',
      headerName: 'Discount',
      width: 120,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value) {
          return (
            <span style={{
              color: '#7c3aed',
              padding: '4px 10px',
              borderRadius: '6px',
              fontWeight: '800',
              fontSize: '13px'
            }}>
              ৳{Number(params.value).toFixed(2)}
            </span>
          );
        }
        return <span style={{ color: '#9ca3af' }}>-</span>;
      }
    },
    
    // Stock
    { 
      field: 'stock', 
      headerName: 'Stock', 
      sortable: true, 
      width: 100,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: any) => {
        const stock = params.value;
        let style: React.CSSProperties;
        let text: string | number;
        
        if (stock > 10) {
          style = {
            color: '#10b981',
            padding: '4px 12px',
            borderRadius: '12px',
            fontWeight: '800',
            fontSize: '12px'
          };
          text = stock;
        } else if (stock > 0) {
          style = {
            color: '#f59e0b',
            padding: '4px 12px',
            borderRadius: '12px',
            fontWeight: '800',
            fontSize: '12px'
          };
          text = stock;
        } else {
          style = {
            background: '#fee2e2',
            color: '#ef4444',
            padding: '4px 12px',
            borderRadius: '12px',
            fontWeight: '800',
            fontSize: '12px'
          };
          text = 'Out';
        }
        
        return <span style={style}>{text}</span>;
      }
    },
    
    // Views Count
    {
      field: 'views_count',
      headerName: 'Views',
      width: 90,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => params.value || 0,
      cellStyle: { color: '#6b7280', fontWeight: '500' }
    },
    
    // Sales Count
    {
      field: 'sales_count',
      headerName: 'Sales',
      width: 90,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => params.value || 0,
      cellStyle: { fontWeight: '600', color: '#3b82f6' }
    },

    // Featured
    {
      field: 'is_featured',
      headerName: 'Featured',
      width: 100,
      sortable: true,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value) {
          return <span style={{ color: '#f59e0b', fontSize: '20px' }} title="Featured">⭐</span>;
        }
        return <span style={{ color: '#d1d5db', fontSize: '20px' }} title="Not Featured">☆</span>;
      }
    },

    // Active Status
    { 
      field: 'is_active', 
      headerName: 'Active', 
      width: 90,
      sortable: true,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: any) => {
        if (params.value) {
          return <span style={{ color: '#10b981', fontSize: '20px', fontWeight: 'bold' }} title="Active">✓</span>;
        }
        return <span style={{ color: '#ef4444', fontSize: '20px', fontWeight: 'bold' }} title="Inactive">✗</span>;
      }
    },
    
    // Created Date
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return date.toLocaleString('en-GB', { 
          day: '2-digit', 
          month: 'short', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
      cellStyle: { color: '#6b7280', fontSize: '12px' }
    },
    
    // Actions
    { 
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit Product"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete Product"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getProducts();
      console.log('Products response:', response); // Debug log
      
      // Handle axios response structure
      let productsList: Product[] = [];
      if (response.data) {
        // If response.data is an array, use it directly
        if (Array.isArray(response.data)) {
          productsList = response.data;
        } 
        // If response.data has a results property (paginated), use that
        else if (response.data.results && Array.isArray(response.data.results)) {
          productsList = response.data.results;
        }
        // If response.data has a data property, use that
        else if (response.data.data && Array.isArray(response.data.data)) {
          productsList = response.data.data;
        }
      }
      // Fallback: if response itself is an array
      else if (Array.isArray(response)) {
        productsList = response;
      }
      
      console.log('Processed products:', productsList); // Debug log
      setProducts(productsList);
    } catch (error) {
      console.error('Failed to load products:', error);
      setToast({ show: true, message: 'Failed to load products', type: 'error' });
      setProducts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await adminAPI.getCategories();
      console.log('Categories for dropdown:', response); // Debug log
      
      // Handle axios response structure
      let categoriesList: Category[] = [];
      if (response.data) {
        // If response.data is an array, use it directly
        if (Array.isArray(response.data)) {
          categoriesList = response.data;
        } 
        // If response.data has a results property (paginated), use that
        else if (response.data.results && Array.isArray(response.data.results)) {
          categoriesList = response.data.results;
        }
        // If response.data has a data property, use that
        else if (response.data.data && Array.isArray(response.data.data)) {
          categoriesList = response.data.data;
        }
      }
      // Fallback: if response itself is an array
      else if (Array.isArray(response)) {
        categoriesList = response;
      }
      
      console.log('Processed categories for dropdown:', categoriesList); // Debug log
      setCategories(categoriesList);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]); // Set empty array on error
    }
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (product: Product) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;
    
    try {
      await adminAPI.deleteProduct(selectedProduct.id);
      setToast({ show: true, message: 'Product deleted successfully', type: 'success' });
      loadProducts();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete product', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      let savedProduct;
      if (selectedProduct) {
        const response = await adminAPI.updateProduct(selectedProduct.id, formData);
        savedProduct = response.data;
        setToast({ show: true, message: 'Product updated successfully', type: 'success' });
      } else {
        const response = await adminAPI.createProduct(formData);
        savedProduct = response.data;
        setToast({ show: true, message: 'Product created successfully', type: 'success' });
      }
      loadProducts();
      return savedProduct; // Return product data with ID for image uploads
    } catch (error: any) {
      console.error('Save error:', error);
      console.error('Error response:', error.response?.data);
      throw error; // Re-throw to let form handle validation errors
    }
  };

  const handleAddNew = () => {
    setSelectedProduct(null);
    setIsModalOpen(true);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure products is always an array before filtering
  const productsArray = Array.isArray(products) ? products : [];
  const filteredProducts = productsArray.filter(product =>
    product.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.name_bn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Product Management</h3>
          <p>Manage your product catalog with advanced grid features</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add New Product
          </button>
          <button className="btn btn-secondary" onClick={loadProducts}>
            🔄 Refresh
          </button>
        </div>
      </div>
      
      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search products by name..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredProducts.length}</span> products found
        </div>
      
        <div className="ag-grid-container">
          <AgGridReact

            theme={themeQuartz}
            rowData={filteredProducts || []}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={20}
          loading={loading}

          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: true,
          }}
          rowHeight={65}
          headerHeight={48}
          animateRows={true}
          rowSelection="single"
          suppressCellFocus={true}
          />
        </div>
      </div>

      <ProductFormModal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setSelectedProduct(null); 
        }}
        onSave={handleSave}
        product={selectedProduct}
        categories={categories}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedProduct(null);
        }}
        onConfirm={handleDelete}
        title="Delete Product"
        message={`Are you sure you want to delete "${selectedProduct?.name_en}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default ProductManagement;
